import { Check } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { PageHero } from "@/components/common/PageHero";
import { Reveal } from "@/components/common/Reveal";
import { PillLink } from "@/components/common/PillLink";
import { CourseArt } from "@/components/common/CourseArt";
import { COURSES } from "@/lib/site";

const CourseDetail = ({ c, i }) => {
  return (
    <Reveal as="article" className="scroll-mt-28" >
      <div id={c.slug} className={`scroll-mt-28 grid items-center gap-10 lg:grid-cols-2 ${i % 2 ? "lg:[&>*:first-child]:order-2" : ""}`} data-testid={`course-detail-${c.slug}`}>
        <div className={`relative overflow-hidden rounded-[40px] border border-ink/10 ${c.color} aspect-[4/3]`}>
          <CourseArt name={c.slug} className="absolute inset-0 h-full w-full p-8" />
        </div>
        <div>
          <p className="eyebrow">Years 5–10{c.pbl && " · Project-based learning"}</p>
          <h2 className="mt-4 text-4xl sm:text-5xl font-semibold tracking-tight">{c.title}</h2>
          <div className="mt-4 space-y-3 text-base leading-relaxed text-ink-soft sm:text-lg" data-testid={`course-description-${c.slug}`}>
            {c.description.map((p) => <p key={p.slice(0, 24)}>{p}</p>)}
          </div>
          <h3 className="mt-8 text-xl font-semibold">What learners explore</h3>
          <ul className="mt-4 grid gap-3 sm:grid-cols-2">
            {c.focus.map((f) => (
              <li key={f} className="flex items-start gap-3">
                <span className={`mt-0.5 grid h-6 w-6 shrink-0 place-items-center rounded-full ${c.color} border border-ink`}><Check className="h-3.5 w-3.5" aria-hidden="true" /></span>
                <span className="font-semibold">{f}</span>
              </li>
            ))}
          </ul>
          <div className="mt-8"><PillLink to="/contact#enquire" variant="primary" arrow data-testid={`course-enquire-${c.slug}`}>Enquire about {c.title}</PillLink></div>
        </div>
      </div>
    </Reveal>
  );
};

export default function Courses() {
  useSeo("Courses", "Explore MUCCI Academy courses for Years 5–10 in New Zealand: Science Inquiry, Social Studies, Engineering and Critical Thinking, all taught through project-based learning.");
  return (
    <div data-testid="courses-page">
      <PageHero testid="courses-hero" eyebrow="Courses" color="bg-mint" title="Courses that start with a question"
        intro="Four inquiry-based courses: Science Inquiry, Social Studies, Engineering and Critical Thinking. All four are taught through project-based learning (PBL), online or in person at MUCCI locations." />
      <nav aria-label="Course list" className="container-x mt-8">
        <ul className="flex flex-wrap gap-3">
          {COURSES.map((c) => (
            <li key={c.slug}><a href={`#${c.slug}`} className={`inline-block rounded-full border border-ink/10 ${c.color} px-5 py-2 font-bold transition-transform duration-200 hover:-translate-y-0.5`} data-testid={`course-jump-${c.slug}`}>{c.title}</a></li>
          ))}
        </ul>
      </nav>
      <div className="container-x space-y-14 section-y">
        {COURSES.map((c, i) => <CourseDetail key={c.slug} c={c} i={i} />)}
      </div>
      <div className="container-x">
        <div className="card-soft p-8 sm:p-10 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <p className="text-lg max-w-2xl"><span className="font-heading text-2xl font-semibold block">Timetables, fees and availability</span>Ask us about current class options for your learner's year level and preferred way to learn.</p>
          <PillLink to="/contact#enquire" variant="lemon" arrow data-testid="courses-bottom-enquire">Send an enquiry</PillLink>
        </div>
      </div>
    </div>
  );
}
